<template>
  <v-col
    v-if="enabled"
    :cols="cols"
    :lg="lg"
    :md="md"
  >
    <v-row v-if="openTitle && closeTitle">
      <v-col
        :class="titleClass || 'fs-18'"
        class="fw-600"
      >
        {{ $bct(openTitle) }}
      </v-col>
      <v-col
        :class="titleClass || 'fs-18'"
        class="fw-600 ta-right"
      >
        {{ $bct(closeTitle) }}
      </v-col>
    </v-row>
    <v-row dense>
      <v-col class="d-flex align-center">
        <div class="fs-15 fw-500 pe-2 ta-left">
          {{ $bct(openText) }}
        </div>
        <v-range-slider
          v-model="timeRange"
          @blur="handleBlur"
          @end="handleChange"
          @update:model-value="handleInput"
          :aria-label="$bct('start and finish time')"
          :disabled="locked"
          :max="maxMins"
          :min="minMins"
          :step="stepValue"
          color="primary"
          hide-details
        />
        <div class="fs-15 fw-500 ps-2 ta-right">
          {{ $bct(closeText) }}
        </div>
      </v-col>
    </v-row>
  </v-col>
</template>

<script setup>
import useLabeledField from '@/shared/composables/useLabeledField';
import useSchematizedField from '@/shared/composables/useSchematizedField';
import useEventBus from '@/shared/composables/useEventBus';
import propsToRefs from '@/shared/utils/propsToRefs';
import { parseTimeToMil, parseMilToTime, parseTimeToString } from '@/shared/services/time-parser';

const $bct = inject('translator');
const eventBus = useEventBus();

const emit = defineEmits(['blur', 'change', 'input', 'update:modelValue']);
const localValue = defineModel({ type: Array });

const props = defineProps({
  maxMins: {
    type: Number,
    default: 1440,
  },
  minMins: {
    type: Number,
    default: 0,
  },
  openTitle: {
    type: String,
    default: null,
  },
  titleClass: {
    type: String,
    default: null,
  },
  closeTitle: {
    type: String,
    default: null,
  },
  stepValue: {
    type: Number,
    default: 15,
  },
  ...useLabeledField.props,
  ...useSchematizedField.props,
});

const defaultTimeRange = [0, 1440];
const timeRange = ref(
  localValue.value
    ? [parseMilToTime(localValue.value[0]), parseMilToTime(localValue.value[1])]
    : [0, 1440],
);

const { enabled, locked } = useSchematizedField(
  ...propsToRefs(props, useSchematizedField.paramKeys),
);

const openText = computed(() => {
  return parseTimeToString(timeRange.value[0]);
});

const closeText = computed(() => {
  return parseTimeToString(timeRange.value[1]);
});

watch(
  () => localValue.value,
  (newVal) => {
    if (!newVal) {
      timeRange.value = defaultTimeRange;
    } else {
      timeRange.value = [parseMilToTime(newVal[0]), parseMilToTime(newVal[1])];
    }
  },
);

function handleBlur() {
  const newVal = [parseTimeToMil(timeRange.value[0]), parseTimeToMil(timeRange.value[1])];
  emit('blur', newVal);
  if (props.global) {
    eventBus.blur(newVal);
  }
}

function handleChange() {
  const newVal = [parseTimeToMil(timeRange.value[0]), parseTimeToMil(timeRange.value[1])];
  emit('change', newVal);
  if (props.global) {
    eventBus.change(newVal);
  }
}

function handleInput() {
  const newVal = [parseTimeToMil(timeRange.value[0]), parseTimeToMil(timeRange.value[1])];
  localValue.value = newVal;
  if (props.global) {
    eventBus.input(newVal);
  }
}
</script>
